import { Button, Modal, Spinner, TextInput } from 'flowbite-react';
import React, { useEffect } from 'react';
import { useReducerAsState } from '../../hooks/useReducerAsState';
import { PostCodeType } from '../../types/post_code';
import { convertWeatherData } from '../../utils/converters';
import { toast } from 'react-toastify';
import { usePostCodeContext } from '../../context/postCodeContext';
import { API_URL } from '../../constants';

interface PostalCodePreviewModalProps {
    open: boolean;
    postCode: PostCodeType | undefined;
    onClose: () => void;
}

interface State {
    isOpen: boolean;
    postCodeInfo: string;
    loading: boolean;
}

const PostalCodePreviewModal = (props: PostalCodePreviewModalProps) => {
    const { refreshWeatherData } = usePostCodeContext();
    const [state, setState] = useReducerAsState<State>({
        isOpen: false,
        postCodeInfo: props.postCode?.info ?? '',
        loading: false
    });

    const { loading } = state;

    const _setOpen = (value: boolean) => setState({ isOpen: value });

    useEffect(() => {
        _setOpen(props.open);
        if (props.postCode?.info === undefined) return;
        _setPostCodeInfo(convertWeatherData(props.postCode.info!));
    }, [props.open, props.postCode]);

    const _setLoading = (value: boolean) => setState({ loading: value });

    const _onClose = () => {
        _setOpen(false);
        props.onClose();
    };

    const _setPostCodeInfo = (info: string) => setState({ postCodeInfo: info });

    const _onRefreshData = async () => {
        if (props.postCode === undefined) {
            toast.error('Debe seleccionar un código postal para refrescar sus datos');
            return;
        }
        _setLoading(true);
        const newPostInfoMessage = await refreshWeatherData(props.postCode.postal_code!);
        _setLoading(false);

        if (typeof newPostInfoMessage === 'string') {
            toast.error(`
                Error, por favor verifique que la latitud y la longitud sean correctas.\n
                ref (${newPostInfoMessage})
            `);
            return;
        }

        const data = JSON.stringify(newPostInfoMessage);
        _setPostCodeInfo(convertWeatherData(data));
    };

    const _onCopy = () => {
        navigator.clipboard.writeText(convertWeatherData(props.postCode?.info ?? ''));
        toast.success('Copiado correctamente');
    };

    const _onCopyEndpoint = () => {
        navigator.clipboard.writeText(`${API_URL}postal-code/${props.postCode?.postal_code}`);
        toast.success('Copiado correctamente');
    };

    return (
        <Modal show={state.isOpen} onClose={_onClose}>
            <Modal.Header>Visualización de información</Modal.Header>
            <Modal.Body>
                <div className="text-center">
                    <div className="w-full">
                        <div className="w-full relative bg-gray-50 rounded-lg dark:bg-gray-700 p-4 h-64">
                            <div className="w-full overflow-scroll max-h-full">
                                {loading && <Spinner size="xl" />}

                                {!loading && (
                                    <pre>
                                        <code
                                            id="code-block"
                                            className="w-full text-sm text-gray-500 dark:text-gray-400 whitespace-pre">
                                            {state.postCodeInfo}
                                        </code>
                                    </pre>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="relative w-full">
                            <TextInput
                                disabled
                                className=" w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-e-lg rounded-s-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500"
                                value={`${API_URL}postal-code/${props.postCode?.postal_code}`}
                            />
                            <Button
                                onClick={_onCopyEndpoint}
                                color="success"
                                className="absolute top-0 right-0 h-full text-sm font-medium text-white  rounded-e-lg border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                                Copiar
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button color="warning" onClick={_onCopy}>
                    Copiar datos
                </Button>
                <Button color="success" onClick={_onRefreshData}>
                    Refrescar datos
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PostalCodePreviewModal;
